import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import ProgressBar from '../../../components/ui/ProgressBar';
import { dailyTaskService } from '../../../services/taskService';
import { workerService } from '../../../services/workerService';
import { useLanguage } from '../../../contexts/LanguageContext';

const TaskProgressPanel = ({ siteId }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [tasks, setTasks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date()?.toISOString()?.split('T')?.[0];
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

  useEffect(() => {
    loadData();
  }, [siteId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksResult, workersResult] = await Promise.all([
        dailyTaskService?.getForDateRange(sevenDaysAgo, today, siteId || null),
        workerService?.getAllWorkers(),
      ]);
      setTasks(tasksResult?.data || []);
      setWorkers((workersResult?.data || [])?.filter(w => w?.role === 'worker'));
    } catch (e) {
      // non-critical
    } finally {
      setLoading(false);
    }
  };

  const total = tasks?.length;
  const completed = tasks?.filter(t => t?.status === 'completed')?.length;
  const blocked = tasks?.filter(t => t?.status === 'blocked')?.length;
  const overdue = dailyTaskService?.getOverdueTasks(tasks)?.length;
  const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
  const workerStats = dailyTaskService?.groupByWorker(tasks, workers);
  const getBarColor = (r) => r >= 80 ? 'bg-emerald-500' : r >= 50 ? 'bg-amber-500' : 'bg-red-500';

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-muted rounded w-1/3" />
          <div className="h-2 bg-muted rounded" />
          <div className="h-16 bg-muted rounded" />
        </div>
      </div>
    );
  }

  const statsRow = [
    { labelKey: 'taskProgressTotal', value: total, color: 'text-foreground' },
    { labelKey: 'taskProgressDone', value: completed, color: 'text-emerald-600' },
    { labelKey: 'taskProgressBlocked', value: blocked, color: 'text-red-600' },
    { labelKey: 'taskProgressOverdue', value: overdue, color: 'text-orange-600' },
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon name="ListTodo" size={18} className="text-accent" />
            <div>
              <h3 className="font-semibold text-foreground font-heading">{t('taskProgressTitle')}</h3>
              <p className="text-xs text-muted-foreground">Last 7 days</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/task-planner')} className="text-xs font-semibold text-accent hover:underline flex items-center gap-1">
              <Icon name="Plus" size={12} />{t('taskProgressPlan')}
            </button>
            <button onClick={() => navigate('/task-completion-workflow' + (siteId ? `?site=${siteId}` : ''))} className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1">
              {t('taskProgressViewAll')} <Icon name="ArrowRight" size={12} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">{t('taskProgressTodayCompletion')}</span>
          <span className={`text-sm font-bold ${rate >= 80 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{rate}%</span>
        </div>
        <ProgressBar value={rate} color={getBarColor(rate)} showValue={false} label={t('taskProgressTodayCompletion')} />
        <div className="grid grid-cols-4 gap-2 mt-3">
          {statsRow?.map(s => (
            <div key={s?.labelKey} className="text-center">
              <p className={`text-lg font-bold ${s?.color}`}>{s?.value}</p>
              <p className="text-xs text-muted-foreground">{t(s?.labelKey)}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="divide-y divide-border">
        {workerStats?.slice(0, 4)?.map(worker => (
          <div key={worker?.id} className="px-5 py-3 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => navigate(`/task-completion-workflow?worker=${worker?.id}`)}>
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-accent">{worker?.full_name?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-foreground truncate">{worker?.full_name}</p>
                  <span className={`text-xs font-bold ml-1 ${worker?.completion_rate >= 80 ? 'text-emerald-600' : worker?.completion_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{worker?.completion_rate}%</span>
                </div>
                <ProgressBar value={worker?.completion_rate} color={getBarColor(worker?.completion_rate)} showValue={false} label={worker?.full_name} />
              </div>
            </div>
          </div>
        ))}
        {workerStats?.length === 0 && (
          <div className="px-5 py-6 text-center text-sm text-muted-foreground">{t('taskProgressNoTasks')}</div>
        )}
      </div>
      {overdue > 0 && (
        <div className="px-5 py-3 bg-orange-50 border-t border-orange-100 flex items-center gap-2 cursor-pointer hover:bg-orange-100 transition-colors" onClick={() => navigate('/task-completion-workflow?status=overdue' + (siteId ? `&site=${siteId}` : ''))}>
          <Icon name="AlertTriangle" size={14} className="text-orange-600" />
          <p className="text-xs font-semibold text-orange-700">
            {overdue !== 1
              ? t('taskProgressOverdueBannerPlural')?.replace('{n}', overdue)
              : t('taskProgressOverdueBanner')?.replace('{n}', overdue)}
          </p>
          <Icon name="ArrowRight" size={12} className="text-orange-600 ml-auto" />
        </div>
      )}
    </div>
  );
};

export default TaskProgressPanel;
